import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { formatVesselVisitIdentifier, PATHS, useAsyncFetch } from '@tom-ui/utils'
import { computed } from 'mobx'
import { BreadcrumpItem } from 'modules/ui/src/theme/components/breadcrumbs/Breadcrumb'
import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'

type BreadcrumbState = {
  customBreadcrumbs?: { label: string; path: string }[]
}

// This hook is used to generate breadcrumbs for the order page (create/edit) only.
// TODO [Order Rework] Utilize visit-utils to get the correct url
export const useOrderBreadcrumbsV2 = () => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { appViewStore, vesselVisitItemStore, railVisitItemStore } = usePlanningStore()

  const location = useLocation()
  const state = location.state as BreadcrumbState | null

  const { pathname } = useLocation() as {
    pathname: string
  }

  const path = pathname.split('/').filter(Boolean)
  const normalize = (value: string) => {
    while (value.startsWith('/')) {
      value = value.slice(1)
    }
    while (value.endsWith('/')) {
      value = value.slice(0, -1)
    }
    return value
  }

  const isVesselVisitRoute = path.includes(normalize(PATHS.visits.vesselVisits.url))
  const isRailVisitRoute = path.includes(normalize(PATHS.visits.railVisits.url))
  const isContainerVisitRoute = path.includes(normalize(PATHS.visits.containerVisits.url))
  const isBookingRoute = path.includes(normalize(PATHS.orders.bookings.url))

  const isInbound = path.includes('discharge')
  const isRestow = path.includes('restow')

  const isNewOrderRoute = path[path.length - 1] === 'new'

  const { visitId: visitIdParam, bookingNumber } = useParams()
  const visitId = Number(visitIdParam)

  const vesselVisit = computed(() => vesselVisitItemStore.elements[visitId]).get()
  const railVisit = computed(() => railVisitItemStore.elements[visitId]).get()

  useAsyncFetch(async () => {
    if (isVesselVisitRoute) {
      await vesselVisitItemStore.fetchById(visitId)
    } else if (isRailVisitRoute) {
      await railVisitItemStore.fetchById(visitId)
    }
  }, [isVesselVisitRoute, isRailVisitRoute, vesselVisitItemStore, visitId, railVisitItemStore])

  useEffect(() => {
    appViewStore.showLoader()

    if (isVesselVisitRoute && vesselVisit) {
      appViewStore.hideLoader()
    } else if (!isVesselVisitRoute && !isRailVisitRoute) {
      appViewStore.hideLoader()
    }
  }, [appViewStore, isRailVisitRoute, isVesselVisitRoute, vesselVisit])

  const orderNewLabel = isRestow
    ? t('createNewRestowOrder', 'Create new restow order')
    : t('createNewOrder', 'Create new order')
  const orderDetailsLabel = t('orderDetails', 'Order details')

  const visitsLabel = t('visits', 'Visits')
  const railVisitLabel = t('railVisits', 'Rail visits')
  const bookingsLabel = t('bookings', 'Bookings')
  const containerVisitsLabel = t('containerVisits', 'Container visits')

  const visitsPath = PATHS.visits.visits.url
  const vesselVisitsPath = PATHS.visits.vesselVisits.url
  const railVisitsPath = PATHS.visits.railVisits.url
  const bookingsPath = PATHS.orders.bookings.url
  const containerVisitsPath = PATHS.visits.containerVisits.url

  const getCarrierVisitLabel = () => {
    if (isVesselVisitRoute && vesselVisit) {
      const carrierName = vesselVisit.vessels[0]?.data.name
      const vesselVisitNumber = vesselVisit.data.number

      if (carrierName && vesselVisitNumber) {
        return formatVesselVisitIdentifier(carrierName, vesselVisitNumber) ?? carrierName
      }
      return carrierName || t('unknownVisit', 'Unknown visit')
    } else if (isRailVisitRoute) {
      return railVisit?.data.name || t('unknownVisit', 'Unknown visit')
    }

    return t('unknownVisit', 'Unknown visit')
  }

  const getCarrierVisitDirectionLabel = () => {
    if (isRestow) {
      return t('restowList', 'Restow list')
    } else if (isInbound) {
      return t('dischargeList', 'Discharge list')
    } else {
      return t('loadList', 'Load list')
    }
  }

  const getCarrierVisitDirectionPathName = () => {
    if (isRestow) {
      return 'restow'
    } else if (isInbound) {
      return 'discharge'
    } else {
      return 'load'
    }
  }

  const getCarrierVisitPathName = () => {
    if (isVesselVisitRoute) {
      return vesselVisitsPath
    } else if (isRailVisitRoute) {
      return railVisitsPath
    }

    return ''
  }

  const getCarrierVisitUrl = () => `${getCarrierVisitPathName()}/${visitId}`

  const getCarrierVisitDashboardUrl = () => `${getCarrierVisitUrl()}/dashboard`

  const getCarrierVisitOrderListUrl = () =>
    `${getCarrierVisitUrl()}/${getCarrierVisitDirectionPathName()}`

  const getCarrierVisitBreadcrumbs = (): BreadcrumpItem[] => {
    const carrierVisitLabel = getCarrierVisitLabel()
    const directionLabel = getCarrierVisitDirectionLabel()

    const carrierVisitDashboardUrl = getCarrierVisitDashboardUrl()
    const carrierVisitOrderListUrl = getCarrierVisitOrderListUrl()
    const vesselVisitsLabel = t('vesselVisits', 'Vessel visits')

    const baseLabel = isVesselVisitRoute
      ? vesselVisitsLabel
      : isRailVisitRoute
        ? railVisitLabel
        : visitsLabel

    const basePath = isVesselVisitRoute
      ? vesselVisitsPath
      : isRailVisitRoute
        ? railVisitsPath
        : visitsPath

    const breadcrumbs: BreadcrumpItem[] = [
      {
        label: baseLabel,
        onClick: () => navigate(basePath),
      },
      {
        label: carrierVisitLabel,
        onClick: () => navigate(carrierVisitDashboardUrl),
      },
      {
        label: directionLabel,
        onClick: () => navigate(carrierVisitOrderListUrl),
      },
      {
        label: isNewOrderRoute ? orderNewLabel : orderDetailsLabel,
      },
    ]

    return breadcrumbs
  }

  const getContainerVisitBreadcrumbs = (): BreadcrumpItem[] => {
    const breadcrumbs: BreadcrumpItem[] = [
      {
        label: containerVisitsLabel,
        onClick: () => navigate(containerVisitsPath),
      },
      {
        label: isNewOrderRoute ? orderNewLabel : orderDetailsLabel,
      },
    ]

    return breadcrumbs
  }

  const getBookingBreadcrumbs = (): BreadcrumpItem[] => {
    if (!bookingNumber) return []

    const breadcrumbs: BreadcrumpItem[] = [
      {
        label: bookingsLabel,
        onClick: () => navigate(bookingsPath),
      },
      {
        label: bookingNumber,
        onClick: () => navigate(`${bookingsPath}/${bookingNumber}`),
      },
      {
        label: isNewOrderRoute ? orderNewLabel : orderDetailsLabel,
      },
    ]

    return breadcrumbs
  }

  const getRouteBreadcrumbs = () => {
    if (state?.customBreadcrumbs && state.customBreadcrumbs.length > 0) {
      return [
        ...state.customBreadcrumbs.map(item => ({
          label: item.label,
          onClick: () => navigate(item.path),
        })),
        {
          label: isNewOrderRoute ? orderNewLabel : orderDetailsLabel,
        },
      ]
    }

    if (isVesselVisitRoute || isRailVisitRoute) {
      return getCarrierVisitBreadcrumbs()
    } else if (isContainerVisitRoute) {
      return getContainerVisitBreadcrumbs()
    } else if (isBookingRoute) {
      return getBookingBreadcrumbs()
    }

    return []
  }

  return { getRouteBreadcrumbs, isVesselVisitRoute, isRailVisitRoute }
}
